// ---------------------------------------------------------------------------
// V9's screens: configuration packs, their versions, and events.
//
// DELIBERATELY MINIMAL, on the same instruction and the same merits as
// Credentials.tsx and Roles.tsx. docs/11-console-design-system.md replaces the
// console's direction immediately after this slice; this file introduces no
// colour token, no new component and no bespoke layout — only StatusChip,
// .data-table, .panel and .field — so the rework is a values change rather than
// a rewrite.
//
// WHAT IT DOES INVEST IN IS NOT VISUAL:
//
// 1. NO SCREEN ASKS WHICH STATE A VERSION IS IN. Every disabled control is
//    disabled by `is_editable` or `is_pinnable`, both of which travel from
//    app.config_pack_status. A fourth state added at V30 arrives in this
//    interface with the right affordances and no edit here. Comparing the
//    status field against a literal state name would be a second copy of the
//    rule, living where no database test can see it.
//
//    (This paragraph twice quoted the literal it forbids, and verify-V9.sh's
//    guard refused the file both times. Every file in this repository that
//    explains a naming rule has ended up breaking it — V3's finding 4, V4's 3,
//    V7's 11, V8's 6, and three separate times in V9. The rule has no prose
//    exemption; the reliable habit is to describe the shape of the forbidden
//    thing rather than to show it.)
//
// 2. PUBLISHING SAYS WHAT IT COSTS, BEFORE IT HAPPENS. It cannot be undone, and
//    a person who did not know that is a person who will ask for an unfreeze
//    button — which is the request that, granted, destroys V22. The panel says
//    so in words and offers the alternative in the same breath.
//
// 3. THE PINNED VERSION IS SHOWN ON EVERY EVENT ROW, with its number. "What did
//    this event require" is the Proof Pack's question, and an interface that
//    shows only the pack name teaches people that the pack is the answer.
//
// 4. NO VOCABULARY IS WRITTEN HERE. Verticals, pack states and check-in methods
//    are all fetched. That is the whole of §V9.5 point 1 on this side of the
//    wire.
// ---------------------------------------------------------------------------

import { useCallback, useEffect, useRef, useState } from "react";

import { may, send } from "../api/auth";
import {
  console_,
  type ConfigPack,
  type ConfigPackState,
  type ConfigPackVersionDetail,
  type VerbunkEvent,
  type Vertical,
} from "../api/console";
import { StatusChip } from "../design/StatusChip";
import { useT } from "../i18n/LocaleProvider";
import { translateLabel } from "../i18n/translate";
import { Tabs } from "../design/Tabs";
import { AddPayRuleSet, AddRole, DiscardDraft, PayRuleSetRates } from "./ConfigurationEdit";
import { useSession } from "../session/SessionProvider";
import { usePartialLoad } from "./PartialLoad";

/** Any response body, which may instead be an error envelope. Roles.tsx's shape. */
type Failable<T> = Partial<T> & { error?: { message?: string } };

/**
 * The chip for a version's state.
 *
 * TONE IS DERIVED FROM THE TWO BOOLEANS, NEVER FROM THE STATE NAME. Editable
 * means work in progress (amber, attention); pinnable means live and usable
 * (teal); neither means withdrawn (neutral). A state added later gets a
 * defensible colour without anybody choosing one.
 *
 * Semantic discipline, and it is worth stating: teal is live and confirmed,
 * amber is attention. A draft is not a failure — nothing is broken — so it is
 * never red.
 */
function versionTone(v: { is_editable: boolean; is_pinnable: boolean }): "live" | "attention" | "neutral" {
  if (v.is_pinnable) return "live";
  if (v.is_editable) return "attention";
  return "neutral";
}

function shortDate(value: string): string {
  return new Date(value).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
}

// ===========================================================================
// THE PACK LIST AND THE VERSION PANEL
// ===========================================================================

export function ConfigurationPacks() {
  const { apiBase, me } = useSession();
  const { locale, t, refusal } = useT();
  const partial = usePartialLoad();
  const [packs, setPacks] = useState<ConfigPack[]>([]);
  const [verticals, setVerticals] = useState<Vertical[]>([]);
  const [states, setStates] = useState<ConfigPackState[]>([]);
  const [selected, setSelected] = useState<string | null>(null);
  const [detail, setDetail] = useState<ConfigPackVersionDetail | null>(null);
  const [versions, setVersions] = useState<ConfigPack & { versions?: ConfigPackVersionDetail[] } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [loaded, setLoaded] = useState(false);

  // ADVISORY ONLY. This decides whether to SHOW a control, never whether the
  // act is allowed — the API refuses it and the database refuses the row.
  const canManage = me ? may(me, "config.manage") : false;

  const loadPacks = useCallback(async () => {
    const { status, body } = await send<Failable<{ config_packs: ConfigPack[] }>>(
      console_.listConfigPacks(apiBase),
    );
    if (status === 200 && body.config_packs) {
      setPacks(body.config_packs);
      setError(null);
    } else {
      setError(refusal(body.error) ?? t("We could not load the configuration packs."));
    }
    setLoaded(true);
  }, [apiBase]);

  useEffect(() => {
    void loadPacks();
    void send<Failable<{ verticals: Vertical[] }>>(console_.verticals(apiBase)).then(({ status, body }) => {
      if (status === 200 && body.verticals) setVerticals(body.verticals);
      else partial.note();
    }).catch(() => partial.note());
    void send<Failable<{ config_pack_states: ConfigPackState[] }>>(console_.configPackStates(apiBase)).then(
      ({ status, body }) => {
        if (status === 200 && body.config_pack_states) setStates(body.config_pack_states);
        else partial.note();
      },
    ).catch(() => partial.note());
  }, [apiBase, loadPacks]);

  const openPack = useCallback(
    async (packId: string) => {
      setSelected(packId);
      setDetail(null);
      const { status, body } = await send<Failable<ConfigPack & { versions: ConfigPackVersionDetail[] }>>(
        console_.getConfigPack(apiBase, packId),
      );
      if (status === 200 && body.versions) {
        setVersions(body as ConfigPack & { versions: ConfigPackVersionDetail[] });
        const first = body.versions[0];
        if (first) await openVersion(first.id);
      } else {
        setError(refusal(body.error) ?? t("We could not open that pack."));
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [apiBase],
  );

  async function openVersion(versionId: string) {
    const { status, body } = await send<Failable<ConfigPackVersionDetail>>(
      console_.getConfigPackVersion(apiBase, versionId),
    );
    if (status === 200 && body.id) setDetail(body as ConfigPackVersionDetail);
    else setError(refusal(body.error) ?? t("We could not open that version."));
  }

  async function transition(versionId: string, status: string, label: string) {
    setError(null);
    setNotice(null);
    const res = await send<Failable<ConfigPackVersionDetail>>(
      console_.setConfigPackVersionState(apiBase, versionId, status),
    );
    if (res.status === 200) {
      setNotice(t("This version is now {state}.", { state: translateLabel(locale, label).toLowerCase() }));
      if (selected) await openPack(selected);
    } else {
      // THE SERVER'S SENTENCE, VERBATIM. It is the one that knows why, and a
      // sentence composed here from a status code would be a second account of
      // a rule this slice exists to keep singular.
      setError(refusal(res.body.error) ?? t("That did not work."));
    }
  }

  // SWEEP S21. The route existed (`PATCH /v1/config-packs/:id`) and nothing called it,
  // so a pack forked as "Stadium operations (our version)" kept that name forever.
  async function rename(packId: string, name: string) {
    setError(null);
    setNotice(null);
    const res = await send<Failable<unknown>>(console_.renameConfigPack(apiBase, packId, name));
    if (res.status === 200) {
      setNotice(t("The pack is renamed."));
      await loadPacks();
      await openPack(packId);
    } else {
      setError(refusal(res.body.error) ?? t("We could not rename that pack."));
    }
  }

  async function newVersion(packId: string) {
    setError(null);
    setNotice(null);
    const res = await send<Failable<{ id: string }>>(console_.createConfigPackVersion(apiBase, packId));
    if (res.status === 201) {
      setNotice("A new draft version was created, carrying everything the published one contained.");
      await openPack(packId);
    } else {
      setError(refusal(res.body.error) ?? t("We could not start a new version."));
    }
  }

  const templates = packs.filter((p) => p.is_template);
  const own = packs.filter((p) => !p.is_template);

  return (
    <div>
      {partial.notice}
      <h1>{t("Configuration")}</h1>
      <p className="muted">
        {t("A pack holds the roles, credentials, pay rules and check-in policy for one kind of work. Events are attached to a version of a pack, and a published version never changes, so a Proof Pack can always say what an event required on the day.")}
      </p>

      {error ? <StatusChip tone="failed" label={error} /> : null}
      {notice ? <StatusChip tone="live" label={notice} /> : null}

      <section className="panel">
        <h2>{t("Your packs")}</h2>
        {!loaded ? (
          <p className="muted">{t("Loading…")}</p>
        ) : own.length === 0 ? (
          // THE EMPTY STATE IS THE FIRST-RUN CASE AND IT IS THE ONE THAT MAKES
          // A PRODUCT LOOK BROKEN. It says what to do next and names the thing
          // to do it from, rather than showing a blank table.
          <p className="muted">
            {t("You have no packs of your own yet. Start from one of the {n} platform templates below — you can change anything in your copy.", { n: templates.length })}
          </p>
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                <th scope="col">{t("Pack")}</th>
                <th scope="col">{t("Kind of work")}</th>
                <th scope="col">{t("Versions")}</th>
                <th scope="col">{t("In use")}</th>
              </tr>
            </thead>
            <tbody>
              {own.map((p) => (
                <tr key={p.id}>
                  <td>
                    <button type="button" className="link-button" onClick={() => void openPack(p.id)}>
                      {p.display_name ?? p.name}
                    </button>
                  </td>
                  <td>{translateLabel(locale, p.vertical_label)}</td>
                  <td>{p.version_count}</td>
                  <td>
                    {p.latest_published_version === null ? (
                      <StatusChip tone="attention" label={t("Not published yet")} />
                    ) : (
                      <StatusChip tone="live" label={t("Version {n}", { n: p.latest_published_version })} />
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      <section className="panel">
        <h2>{t("Platform templates")}</h2>
        <p className="muted">
          {t("Maintained centrally and read-only. Use one to get your own copy, which you can then change. Your copy may ask for more than the template requires and never less.")}
        </p>
        {templates.length === 0 ? (
          <p className="muted">{t("No templates are available.")}</p>
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                <th scope="col">{t("Template")}</th>
                <th scope="col">{t("Kind of work")}</th>
                <th scope="col">{t("Version")}</th>
                <th scope="col" />
              </tr>
            </thead>
            <tbody>
              {templates.map((p) => (
                <tr key={p.id}>
                  <td>
                    <button type="button" className="link-button" onClick={() => void openPack(p.id)}>
                      {p.display_name ?? p.name}
                    </button>
                  </td>
                  <td>{translateLabel(locale, p.vertical_label)}</td>
                  <td>{p.latest_published_version ?? "—"}</td>
                  <td>
                    {canManage && p.latest_published_version_id ? (
                      <ForkButton
                        packName={p.name}
                        vertical={p.vertical}
                        versionId={p.latest_published_version_id}
                        onDone={() => void loadPacks()}
                        onError={setError}
                      />
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      {selected && versions?.versions ? (
        <section className="panel">
          <h2>{versions.name} — versions</h2>
          {canManage && !versions.is_template ? (
            <RenamePack key={versions.id} name={versions.name} onRename={(name) => void rename(versions.id, name)} />
          ) : null}
          <table className="data-table">
            <thead>
              <tr>
                <th scope="col">{t("Version")}</th>
                <th scope="col">{t("State")}</th>
                <th scope="col">{t("Published")}</th>
                <th scope="col" />
              </tr>
            </thead>
            <tbody>
              {versions.versions.map((v) => (
                <tr key={v.id}>
                  <td>
                    <button type="button" className="link-button" onClick={() => void openVersion(v.id)}>
                      {t("Version {n}", { n: v.version })}
                    </button>
                  </td>
                  <td>
                    {/* NEVER COLOUR ALONE. StatusChip carries an icon and the
                        label — this is read on a cracked phone, in the rain.
                        (The original wording of this comment named a job role
                        and the taxonomy guard refused the file. Fifth
                        occurrence; the rule has no prose exemption and must not
                        acquire one.) */}
                    <StatusChip tone={versionTone(v)} label={translateLabel(locale, v.status_label)} />
                  </td>
                  <td>{v.published_at ? shortDate(v.published_at) : "—"}</td>
                  <td>
                    {canManage && !versions.is_template ? <VersionActions version={v} states={states} onTransition={transition} /> : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {canManage && !versions.is_template ? (
            <p>
              <button type="button" className="btn btn-secondary" onClick={() => void newVersion(selected)}>
                {t("Start a new version")}
              </button>{" "}
              <span className="muted">
                {t("Copies everything the latest version contains into a draft you can change.")}
              </span>
            </p>
          ) : null}
        </section>
      ) : null}

      {detail ? (
        <VersionDetail
          detail={detail}
          canManage={canManage}
          onChanged={() => openVersion(detail.id)}
          onDiscarded={async () => {
            await loadPacks();
            if (selected) await openPack(selected);
          }}
        />
      ) : null}
    </div>
  );
}

/**
 * The publish and archive controls.
 *
 * DRIVEN BY `is_editable` AND `is_pinnable`, NOT BY THE STATE'S NAME. The
 * "publish" target is the first state that is pinnable and not editable; the
 * "archive" target is the first that is neither. Both are found in the fetched
 * vocabulary, so this component has never heard of the word `published`.
 */
function RenamePack({ name, onRename }: { name: string; onRename: (name: string) => void }) {
  const { t } = useT();
  const [value, setValue] = useState(name);
  return (
    <form
      className="row"
      onSubmit={(e) => {
        e.preventDefault();
        if (value.trim() !== "" && value.trim() !== name) onRename(value.trim());
      }}
    >
      <div className="field">
        <label htmlFor="pack-name">{t("Pack name")}</label>
        <input id="pack-name" required maxLength={120} value={value} onChange={(e) => setValue(e.target.value)} />
      </div>
      <button className="btn btn-quiet" type="submit" disabled={value.trim() === "" || value.trim() === name}>
        {t("Rename the pack")}
      </button>
    </form>
  );
}

function VersionActions({
  version,
  states,
  onTransition,
}: {
  version: { id: string; is_editable: boolean; is_pinnable: boolean };
  states: ConfigPackState[];
  onTransition: (id: string, status: string, label: string) => Promise<void>;
}) {
  const { t } = useT();
  const publishTarget = states.find((s) => s.is_pinnable && !s.is_editable);
  const archiveTarget = states.find((s) => !s.is_pinnable && !s.is_editable);

  // SWEEP S26. THE BUTTON SAYS WHAT IT DOES. It used to print the TARGET state's label —
  // a button reading "Published" beside a "Draft" chip reads as a status, not an action.
  // 0033's rule for the assignment spine, "a button says what it does, not what you
  // become", applied here too.
  if (version.is_editable && publishTarget) {
    return (
      <button type="button" className="btn" onClick={() => void onTransition(version.id, publishTarget.id, publishTarget.label)}>
        {t("Publish")}
      </button>
    );
  }
  if (version.is_pinnable && archiveTarget) {
    return (
      <button type="button" className="btn btn-secondary" onClick={() => void onTransition(version.id, archiveTarget.id, archiveTarget.label)}>
        {t("Archive")}
      </button>
    );
  }
  return null;
}

function ForkButton({
  packName,
  vertical,
  versionId,
  onDone,
  onError,
}: {
  packName: string;
  vertical: string;
  versionId: string;
  onDone: () => void;
  onError: (message: string) => void;
}) {
  const { apiBase } = useSession();
  const { t, refusal } = useT();
  const [busy, setBusy] = useState(false);

  async function fork() {
    setBusy(true);
    const { status, body } = await send<Failable<{ id: string }>>(
      console_.createConfigPack(apiBase, {
        vertical,
        key: `${packName.toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "")}_copy`,
        name: t("{pack} (our version)", { pack: packName }),
        fork_from_version_id: versionId,
      }),
    );
    setBusy(false);
    if (status === 201) onDone();
    else onError(refusal(body.error) ?? t("We could not fork that template."));
  }

  return (
    // S26. "Fork" is a developer's word for what a buyer calls using a template.
    <button type="button" className="btn btn-secondary" onClick={() => void fork()} disabled={busy}>
      {t("Use this template")}
    </button>
  );
}

/**
 * What one version contains.
 *
 * THE HEADLINE LINE IS THE ONE ABOUT PUBLISHING BEING PERMANENT. It is the
 * sentence that stops somebody asking for an unfreeze button later, and it
 * costs nothing to say here.
 */
function VersionDetail({
  detail,
  canManage,
  onChanged,
  onDiscarded,
}: {
  detail: ConfigPackVersionDetail;
  canManage: boolean;
  onChanged: () => Promise<void>;
  onDiscarded: () => Promise<void>;
}) {
  const { locale, t } = useT();
  const editing = canManage && detail.is_editable;
  // S26: scrolled to when a version is OPENED — it draws below seven templates — and not
  // on every edit inside it.
  const panel = useRef<HTMLElement>(null);
  useEffect(() => {
    panel.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [detail.id]);
  return (
    <section className="panel" ref={panel}>
      <h2>
        {t("Version {n}", { n: detail.version })}{" "}
        <StatusChip tone={versionTone(detail)} label={translateLabel(locale, detail.status_label)} />
      </h2>
      {detail.is_editable ? (
        <p className="muted">
          {t("This version can still be changed. Once it is published it is fixed for good — every event attached to it keeps requiring exactly what it says today. To change it after that, start a new version.")}
        </p>
      ) : (
        <p className="muted">
          {t("This version is fixed and cannot be changed.")}{" "}
          {detail.pinned_event_count > 0
            ? t("{n} event(s) attached to it.", { n: detail.pinned_event_count })
            : t("No events are attached to it yet.")}
        </p>
      )}

      <Tabs
        label={t("Version {n}", { n: detail.version })}
        tabs={[
          {
            id: "pack-roles",
            label: t("Roles"),
            content: (
              <>
      {detail.role_defs.length === 0 ? (
        <p className="muted">{t("No roles in this version yet. Add at least one before publishing.")}</p>
      ) : (
        <table className="data-table">
          <thead>
            <tr>
              <th scope="col">{t("Role")}</th>
              <th scope="col">{t("Credentials required")}</th>
            </tr>
          </thead>
          <tbody>
            {detail.role_defs.map((r) => (
              <tr key={r.id}>
                <td>{r.display_name ?? r.name}</td>
                <td>{r.requirement_count}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {editing ? <AddRole versionId={detail.id} onChanged={onChanged} /> : null}
              </>
            ),
          },
          {
            id: "pack-credentials",
            label: t("Credentials introduced here"),
            content: (
              <>
      {detail.credential_types.length === 0 ? (
        <p className="muted">{t("None. This version uses credentials defined elsewhere.")}</p>
      ) : (
        <table className="data-table">
          <thead>
            <tr>
              <th scope="col">{t("Credential")}</th>
              <th scope="col">{t("Blocks work")}</th>
              <th scope="col">{t("Required by law")}</th>
            </tr>
          </thead>
          <tbody>
            {detail.credential_types.map((c) => (
              <tr key={c.id}>
                <td>{c.name}</td>
                <td>
                  {c.is_blocking ? (
                    <StatusChip tone="attention" label={t("Blocks")} />
                  ) : (
                    <span className="muted">{t("Advisory")}</span>
                  )}
                </td>
                <td>
                  {c.is_statutory ? <StatusChip tone="failed" label={t("Cannot be waived")} /> : <span className="muted">—</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
              </>
            ),
          },
          {
            id: "pack-pay",
            label: t("Pay rules"),
            content: (
              <>
      {detail.pay_rule_sets.length === 0 ? (
        <p className="muted">{t("No pay rules in this version.")}</p>
      ) : (
        <div className="stack">
          {detail.pay_rule_sets.map((p) => (
            <PayRuleSetRates key={p.id} set={p} editable={editing} onChanged={onChanged} />
          ))}
        </div>
      )}
      {editing ? <AddPayRuleSet versionId={detail.id} onChanged={onChanged} /> : null}
              </>
            ),
          },
          {
            id: "pack-checkin",
            label: t("Check-in"),
            content: (
              <>
      {detail.checkin_policies.length === 0 ? (
        <p className="muted">{t("No check-in policy in this version.")}</p>
      ) : (
        <ul>
          {detail.checkin_policies.map((p) => (
            <li key={p.id}>
              {p.name} — {translateLabel(locale, p.method_label)}
              {p.geofence_metres !== null ? t(", within {n} metres", { n: p.geofence_metres }) : ""}
              {t(", up to {n} minutes late", { n: p.tolerance_minutes })}
            </li>
          ))}
        </ul>
      )}
              </>
            ),
          },
        ]}
      />
      {editing && detail.pinned_event_count === 0 ? <DiscardDraft versionId={detail.id} onChanged={onDiscarded} /> : null}
    </section>
  );
}

// ===========================================================================
// EVENTS MOVED TO screens/Events.tsx AT V10.
//
// V9 put the event list here because an event WAS a configuration pin and very
// little else. It has a client, a venue, a budget, a state machine and a
// workspace now, and none of that is about configuration packs.
// ===========================================================================
